/**
 * Upload Service
 *
 * REST client for upload intent/complete endpoints,
 * plus Firebase Storage SDK for the actual file upload.
 *
 * Flow: createUploadIntent → uploadFileToStorage (SDK) → completeUpload
 */

import { auth, storage } from '../config/firebase';
import { ref, uploadBytesResumable } from 'firebase/storage';

const isDevelopment =
  window.location.hostname === 'localhost' ||
  window.location.hostname === '127.0.0.1' ||
  window.location.hostname === '0.0.0.0';

const UPLOAD_BASE_URL = isDevelopment
  ? 'http://localhost:5001/planapus-d83dd/us-central1/api/uploads'
  : '/api/uploads';

async function getAuthToken(): Promise<string> {
  const token = await auth.currentUser?.getIdToken();
  if (!token) throw new Error('Not authenticated');
  return token;
}

// ============================================================================
// Intent
// ============================================================================

export interface UploadIntentResponse {
  tempUploadId: string;
  storagePath: string;
  expiresAt: string;
}

export async function createUploadIntent(
  fileName: string,
  mimeType: string,
  sizeBytes: number
): Promise<UploadIntentResponse> {
  const token = await getAuthToken();
  const res = await fetch(`${UPLOAD_BASE_URL}/intents`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ fileName, mimeType, sizeBytes }),
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `Upload intent failed (${res.status})`);
  }

  return res.json();
}

// ============================================================================
// Upload to Storage via Firebase client SDK
// ============================================================================

export function uploadFileToStorage(
  storagePath: string,
  file: File,
  onProgress?: (percent: number) => void
): Promise<void> {
  return new Promise((resolve, reject) => {
    const storageRef = ref(storage, storagePath);
    const uploadTask = uploadBytesResumable(storageRef, file, {
      contentType: file.type,
    });

    uploadTask.on(
      'state_changed',
      (snapshot) => {
        if (onProgress) {
          const percent = Math.round(
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100
          );
          onProgress(percent);
        }
      },
      (error) => {
        reject(new Error(`Upload failed: ${error.message}`));
      },
      () => {
        resolve();
      }
    );
  });
}

// ============================================================================
// Complete
// ============================================================================

export interface UploadCompleteResponse {
  tempUploadId: string;
  status: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
}

export async function completeUpload(
  tempUploadId: string
): Promise<UploadCompleteResponse> {
  const token = await getAuthToken();
  const res = await fetch(`${UPLOAD_BASE_URL}/${tempUploadId}/complete`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `Complete upload failed (${res.status})`);
  }

  return res.json();
}
